var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useContext, useMemo } from "react";
import { useGetList, useGetOne, useSidebarState } from "react-admin";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { ContestsOrder, getSlateName, sortContestList, translateFromPresentation, } from "@sequentech/ui-core";
import ElectionHeader from "@/components/ElectionHeader";
import { Tabs } from "@/components/Tabs";
import { ResourceListStyles } from "@/components/styles/ResourceListStyles";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { IPermissions } from "@/types/keycloak";
import { readSlatesConfig } from "@/utils/slates";
import { SlateDataForm } from "./SlateDataForm";
import { slatePath } from "./slatePaths";
var ALL_RECORDS = { page: 1, perPage: 9999 };
var SlateDataTab = function (_a) {
    var candidates = _a.candidates, props = __rest(_a, ["candidates"]);
    return candidates ? _jsx(SlateDataForm, __assign({ candidates: candidates }, props)) : _jsx(CircularProgress, {});
};
var toSortableContest = function (contest) {
    var _a, _b, _c, _d, _e;
    return ({
        id: contest.id,
        tenant_id: contest.tenant_id,
        election_event_id: contest.election_event_id,
        election_id: contest.election_id,
        max_votes: (_a = contest.max_votes) !== null && _a !== void 0 ? _a : 1,
        min_votes: (_b = contest.min_votes) !== null && _b !== void 0 ? _b : 0,
        winning_candidates_num: (_c = contest.winning_candidates_num) !== null && _c !== void 0 ? _c : 1,
        is_encrypted: (_d = contest.is_encrypted) !== null && _d !== void 0 ? _d : false,
        candidates: [],
        presentation: (_e = contest.presentation) !== null && _e !== void 0 ? _e : undefined,
    });
};
export var SlatePage = function () {
    var _a, _b;
    var _c = useParams(), electionId = _c.id, slateIdParam = _c.slateId;
    var _d = useTranslation(), t = _d.t, i18n = _d.i18n;
    var navigate = useNavigate();
    var tenantId = useTenantStore()[0];
    var isSidebarOpen = useSidebarState()[0];
    var authContext = useContext(AuthContext);
    var aliasRenderer = useAliasRenderer();
    var newSlateId = useMemo(function () { return uuidv4(); }, [electionId]);
    var slateId = slateIdParam !== null && slateIdParam !== void 0 ? slateIdParam : newSlateId;
    var _e = useGetOne("sequent_backend_election", { id: electionId }, { enabled: !!electionId }), election = _e.data, isElectionLoading = _e.isLoading;
    var contests = useGetList("sequent_backend_contest", { filter: { tenant_id: tenantId, election_id: electionId }, pagination: ALL_RECORDS }, { enabled: !!tenantId && !!electionId }).data;
    var candidates = useGetList("sequent_backend_candidate", {
        filter: { tenant_id: tenantId, election_event_id: election === null || election === void 0 ? void 0 : election.election_event_id },
        pagination: ALL_RECORDS,
    }, { enabled: !!tenantId && !!(election === null || election === void 0 ? void 0 : election.election_event_id) }).data;
    var canView = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_DATA_TAB);
    var canEdit = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_WRITE);
    var hasPermissionLabel = !(election === null || election === void 0 ? void 0 : election.permission_label) ||
        !authContext.permissionLabels ||
        authContext.permissionLabels.includes(election.permission_label);
    var presentation = election === null || election === void 0 ? void 0 : election.presentation;
    var defaultLanguage = (_b = (_a = presentation === null || presentation === void 0 ? void 0 : presentation.language_conf) === null || _a === void 0 ? void 0 : _a.default_language_code) !== null && _b !== void 0 ? _b : "en";
    var config = useMemo(function () { return readSlatesConfig(election === null || election === void 0 ? void 0 : election.annotations); }, [election === null || election === void 0 ? void 0 : election.annotations]);
    var slate = slateIdParam ? config === null || config === void 0 ? void 0 : config.slates.find(function (_a) {
        var id = _a.id;
        return id === slateIdParam;
    }) : undefined;
    var orderedContests = useMemo(function () {
        var _a;
        var byId = new Map((contests !== null && contests !== void 0 ? contests : []).map(function (contest) { return [contest.id, contest]; }));
        return sortContestList((contests !== null && contests !== void 0 ? contests : []).map(toSortableContest), (_a = presentation === null || presentation === void 0 ? void 0 : presentation.contests_order) !== null && _a !== void 0 ? _a : ContestsOrder.ALPHABETICAL)
            .map(function (_a) {
            var id = _a.id;
            return byId.get(id);
        })
            .filter(function (contest) { return !!contest; });
    }, [contests, presentation === null || presentation === void 0 ? void 0 : presentation.contests_order]);
    var candidateOptions = useMemo(function () {
        return (candidates !== null && candidates !== void 0 ? candidates : [])
            .map(function (candidate) { return ({
            id: candidate.id,
            contest_id: candidate.contest_id,
            presentation: candidate.presentation,
            label: translateFromPresentation(candidate, "name", i18n.language) || candidate.id,
        }); })
            .sort(function (a, b) { return a.label.localeCompare(b.label, i18n.language); });
    }, [candidates, i18n.language]);
    var contestName = useCallback(function (contest) { return aliasRenderer(contest, defaultLanguage); }, [aliasRenderer, defaultLanguage]);
    var onSaved = useCallback(function (savedId) {
        if (electionId) {
            navigate(slatePath(electionId, savedId), { replace: true });
        }
    }, [electionId, navigate]);
    if (isElectionLoading) {
        return _jsx(CircularProgress, {});
    }
    var isMissing = !!slateIdParam && !slate;
    if (!election || !canView || !hasPermissionLabel || isMissing) {
        return (_jsx(ResourceListStyles.EmptyBox, { children: _jsx(Typography, { variant: "h4", paragraph: true, children: isMissing
                    ? t("slateScreen.common.notFound")
                    : t("electionScreen.common.noPermission") }) }));
    }
    return (_jsxs(Box, { sx: {
            maxWidth: "calc(100vw - ".concat(isSidebarOpen ? "352px" : "96px", ")"),
            bgcolor: "background.paper",
        }, className: "slate-box", children: [_jsx(ElectionHeader, { title: slate
                    ? getSlateName(slate, i18n.language, defaultLanguage)
                    : String(t("slateScreen.common.newSlate")), subtitle: "slateScreen.common.subtitle" }), _jsx(Box, { sx: { bgcolor: "background.paper" }, children: _jsx(Tabs, { elements: [
                        {
                            label: t("slateScreen.tabs.data"),
                            component: SlateDataTab,
                            props: {
                                election: election,
                                slateId: slateId,
                                slate: slate,
                                config: config,
                                contests: orderedContests,
                                contestName: contestName,
                                candidates: contests ? candidates : undefined,
                                candidateOptions: candidateOptions,
                                defaultLanguage: defaultLanguage,
                                canEdit: canEdit,
                                onSaved: onSaved,
                            },
                        },
                    ] }) })] }));
};
